import {
	setSetupLoading,
	setSetupErrorMessage,
	setMonitoringTimeline,
	addMonitoringTimeline,
	deleteMonitoringTimeline,
	setCrossKeyStageGradepointNames,
} from '..';
import {
	getGroupGradepoints as getGroupGradepointsAPI,
	getGroupGradepointsForAccessId,
	updateGroupGradepoints as updateGroupGradepointsAPI,
	addGroupGradepoints as addGroupGradepointsAPI,
	deleteGroupGradepoints as deleteGroupGradepointsAPI,
	lockUpdateGroupGradepoints as lockUpdateGroupGradepointsAPI,
} from '../../../../../api/groupsSetupAPI';
import { getGroupMonitoringPoints, getGroupAccessRecords, getGroupAccessId } from '../selectors';

/**
 * Fetch Group Monitoring Points and store them in Redux
 */
export const fetchGroupMonitoringPoints = (): AppThunk => async (dispatch) => {
	dispatch(setSetupLoading());

	try {
		const responseBuilder = await getGroupGradepointsAPI();

		// Handle response builder errors
		if (responseBuilder.HasError) dispatch(setSetupErrorMessage(responseBuilder.Errors[0].Message));

		// Map api response to a structure suitable for Redux
		const monitoringPoints: Array<Groups.Setup.MonitoringPoint> = responseBuilder.ResponseObject.map(
			(x: Groups.Setup.MonitoringPoint) => {
				return {
					...x,
					isNew: false,
					isEdited: false,
					isDeleted: false,
					lockHasChanged: false,
				} as Groups.Setup.MonitoringPoint;
			}
		);

		dispatch(setMonitoringTimeline(monitoringPoints));
	} catch (err) {
		dispatch(setSetupErrorMessage(err.message));
	}
};

/**
 * Fetch gradepoint names from all key stages within the current academic year and store
 * them in Redux for real-time duplicate name validation across key stages (e.g. KS4 + KS5).
 *
 * Scoped to the current academic year only — gradepoint names from past years do not
 * count as duplicates.
 */
export const fetchCrossKeyStageGradepointNames = (): AppThunk => async (dispatch, getState) => {
	const state = getState();
	const accessRecords = getGroupAccessRecords(state);
	const groupAccessId = getGroupAccessId(state);

	const currentRecord = accessRecords.find((r) => r.id === groupAccessId);

	// Clear stale names immediately so the modal never validates against a previous
	// key stage's list while the new fetch is in-flight
	dispatch(setCrossKeyStageGradepointNames([]));

	// Bail out if no group is selected yet (e.g. on first load before access records arrive)
	if (!currentRecord) return;

	// Restrict to access records sharing the same academic year so that names from
	// previous years do not incorrectly block new gradepoints
	const sameYearRecords = accessRecords.filter(
		(r) => r.academicYear === currentRecord.academicYear
	);

	try {
		// Fan out one request per key stage (there is currently no existing API call to get all
		// gradepoint names for a single group); run in parallel to minimise latency.
		// Groups have at most two key stages (KS4 and KS5), so this is never more than 2 concurrent requests
		const results = await Promise.all(
			sameYearRecords.map((r) => getGroupGradepointsForAccessId(r.id))
		);

		// Partial failures are tolerated — use whatever succeeded rather than
		// blocking the user entirely if one record is temporarily unavailable
		const successfulResults = results.filter((r) => !r.HasError);

		// If every request failed there is nothing usable; surface the first error
		// rather than silently writing an empty list (which would disable validation)
		if (successfulResults.length === 0 && results.length > 0) {
			dispatch(setSetupErrorMessage(results[0].Errors[0].Message));
			return;
		}

		const allNames = successfulResults.flatMap((r) => r.ResponseObject.map((gp) => gp.name));
		dispatch(setCrossKeyStageGradepointNames(allNames));
	} catch (err) {
		dispatch(setSetupErrorMessage((err as Error).message));
	}
};

/**
 * Add or update a monitoring point in Redux
 * @param monitoringPoint The monitoring point to create
 */
export const addMonitoringPoint = (
	monitoringPoint: Groups.Setup.MonitoringPoint
): AppThunk => async (dispatch, getState) => {
	const state = getState();

	const existing = getGroupMonitoringPoints(state)?.find(({ id }) => id === monitoringPoint.id);

	const mp: Groups.Setup.MonitoringPoint = {
		...monitoringPoint,
		isNew: existing ? existing.isNew || false : true,
		isEdited: true,
		isDeleted: false,
		lockHasChanged: existing && existing.locked != monitoringPoint.locked,
	};

	dispatch(addMonitoringTimeline(mp));
};

/**
 * Mark a monitoring point as deleted in Redux
 * @param monitoringPoint The monitoring point to delete
 */
export const deleteMonitoringPoint = (
	monitoringPoint: Groups.Setup.MonitoringPoint
): AppThunk => async (dispatch) => {
	dispatch(
		deleteMonitoringTimeline({
			...monitoringPoint,
			isDeleted: true,
		})
	);
};

/**
 * Thunk that will save all monitoring point changes in one go.
 * Including new grade points, updated grade points and deleted grade points
 */
export const SaveMonitoringPointChanges = (): AppThunk => async (dispatch, getState) => {
	const state = getState();

	const monitoringPoints = getGroupMonitoringPoints(state);

	// Newly created groups that need to be created
	const newMonitoringPointGroups: Array<Groups.Setup.MonitoringPoint> | undefined =
		monitoringPoints?.filter((x) => x.isNew && !x.isDeleted) ?? [];

	// Any existing items to be saved
	const existingMonitoringPoints: Array<Groups.Setup.MonitoringPoint> | undefined =
		monitoringPoints?.filter((x) => x.isEdited && !x.isDeleted && !x.isNew && !x.lockHasChanged) ??
		[];

	// Items where lock status has changed
	const deletedMonitoringPointGroups: Array<Groups.Setup.MonitoringPoint> | undefined =
		monitoringPoints?.filter((x) => x.isDeleted) ?? [];

	// Items where lock status has changed
	const lockUpdateMonitoringPointGroups = monitoringPoints?.filter((x) => x.lockHasChanged) ?? [];

	// Make all requests in one go
	const [create, update, remove] = await Promise.all([
		newMonitoringPointGroups.length ? addGroupGradepointsAPI(newMonitoringPointGroups) : null,

		existingMonitoringPoints.length ? updateGroupGradepointsAPI(existingMonitoringPoints) : null,

		lockUpdateMonitoringPointGroups.length
			? lockUpdateGroupGradepointsAPI(lockUpdateMonitoringPointGroups)
			: null,

		deletedMonitoringPointGroups.length
			? deleteGroupGradepointsAPI(deletedMonitoringPointGroups.map((x) => x.id))
			: null,
	]);

	if ((create && create.HasError) || (update && update.HasError) || (remove && remove.HasError)) {
		const errors = [
			...(create ? create.Errors : []),
			...(update ? update.Errors : []),
			...(remove ? remove.Errors : []),
		];

		dispatch(setSetupErrorMessage(errors[0].Message));
	} else {
		dispatch(fetchGroupMonitoringPoints());
		dispatch(fetchCrossKeyStageGradepointNames());
	}
};
